// Import the polyfills before React since React 16 requires Map and Set to work on IE < 11
import './polyfills'

import React from 'react'
import ReactDOM from 'react-dom'
import URI from 'urijs'

import { Provider } from 'react-redux'
import { isFunction } from 'lodash-es'
import thunkMiddleware from 'redux-thunk'
import { reducer as formReducer } from 'redux-form'
import { createStore } from 'redux-dynamic-modules'
import { getSagaExtension } from 'redux-dynamic-modules-saga'
import promiseMiddleware from 'redux-promise'
import { createBrowserHistory } from 'history'
import { createLogger } from 'redux-logger'
import {
  ConnectedRouter,
  connectRouter,
  routerMiddleware,
} from 'connected-react-router'

import { IntlProvider } from 'react-intl'
import { generateIntl } from '../lang/intl'
import useLocale from '../shared/hooks/useLocale'

import {
  apiMiddleware,
  sentryMiddleware,
  sentryReportError,
  actionsBlacklist,
} from '../shared/utils'

import App from './App'
import siteReducer from './reducers'
import siteSagas from './sagas.siteEntry'

// Import common features modules
import timezonesModule from '../shared/features/timezones'
import countriesModule from '../shared/features/countries'

// set up webpack publicPath that will be used in runtime
/* eslint-disable-next-line */
__webpack_public_path__ = window.__webpack_public_path__ || '/resources/um/script/react/'

// Disable pushState
if (process.env.NODE_ENV === 'production' && window.pushState.disable) {
  window.pushState.disable()
}

const reduxMiddlewares = [promiseMiddleware, apiMiddleware, thunkMiddleware]

const { redux_logger: reduxLogger } = URI.parseQuery(
  URI(window.location.href).search()
)

if (process.env.NODE_ENV !== 'production' || reduxLogger === '1') {
  const logger = createLogger({
    predicate: (getState, action) => {
      return !actionsBlacklist.includes(action.type)
    },
    collapsed: true,
  })

  reduxMiddlewares.unshift(logger)
}

if (isFunction(sentryMiddleware)) {
  reduxMiddlewares.unshift(sentryMiddleware)
}

const history = createBrowserHistory()
const store = createStore(
  {
    extensions: [
      getSagaExtension(undefined, (err) => {
        sentryReportError(err)
        throw err
      }),
    ],
  },
  {
    id: 'site',
    reducerMap: { site: siteReducer },
    sagas: [siteSagas],
    middlewares: reduxMiddlewares,
  },
  {
    id: 'form',
    reducerMap: { form: formReducer },
  },
  {
    id: 'router',
    reducerMap: { router: connectRouter(history) },
    middlewares: [routerMiddleware(history)],
  },
  timezonesModule,
  countriesModule
)

let reactContainerNode = document.querySelector('.react-container')
if (!reactContainerNode) {
  reactContainerNode = document.getElementById('main_container')
}

async function renderApp() {
  const { loadMessages, locale, defaultLocale } = useLocale.getState()
  const messages = await loadMessages()

  generateIntl({ locale, messages })

  ReactDOM.render(
    <Provider store={store}>
      <ConnectedRouter history={history}>
        <IntlProvider
          messages={messages}
          locale={locale}
          defaultLocale={defaultLocale}
        >
          <App />
        </IntlProvider>
      </ConnectedRouter>
    </Provider>,
    reactContainerNode
  )
}

// Re-render app when locale changes
useLocale.subscribe(renderApp, (state) => state.locale)

renderApp()
